import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { Home } from './routes/Home';
import { Checkin } from './routes/Checkin';
import { LeaveSubmit } from './routes/LeaveSubmit';
import { OTSubmit } from './routes/OTSubmit';
import { Quota } from './routes/Quota';
import { Policies } from './routes/Policies';
import { Slip } from './routes/Slip';
import { Pair } from './routes/Pair';
import { Claim } from './routes/Claim';
import { Landing } from './routes/Landing';
import { Signup } from './routes/Signup';
import { Profile } from './routes/Profile';
import { RespondInfo } from './routes/RespondInfo';
import { OwnerDashboard } from './routes/owner/Dashboard';
import { OwnerOnboard } from './routes/owner/Onboard';
import { OwnerDriveConfig } from './routes/owner/DriveConfig';
import { PayrollCycle } from './routes/owner/PayrollCycle';
// OwnerReconcile / CloseReadiness / OwnerPayroll merged into PayrollCycle hub —
// the three /owner/* routes below redirect into the hub with the matching tab.
import { PayrollAnalysis } from './routes/owner/PayrollAnalysis';
import { OwnerAudit } from './routes/owner/Audit';
import { OwnerPendingChanges } from './routes/owner/PendingChanges';
import { OwnerDeductions } from './routes/owner/Deductions';
import { OwnerSettings } from './routes/owner/Settings';
import { AISettings } from './routes/owner/AISettings';
import { OwnerYearEnd } from './routes/owner/YearEnd';
import { OwnerTermination } from './routes/owner/Termination';
import { HRDashboard } from './routes/hr/Dashboard';
// /hr/pending merged into the /approvals hub — old route now redirects.
import { HREmployees } from './routes/hr/Employees';
import { People } from './routes/hr/People';
import { Time } from './routes/hr/Time';
import { Wage } from './routes/owner/Wage';
import { OwnerTemplates } from './routes/owner/Templates';
import { WorkflowGuide } from './routes/owner/WorkflowGuide';
import { OwnerBlueprint } from './routes/owner/Blueprint';
import { OwnerSetup } from './routes/owner/Setup';
import { OwnerPolicies } from './routes/owner/Policies';
import { OwnerPayrollHistory } from './routes/owner/PayrollHistory';
import { OwnerHrPermissions } from './routes/owner/HrPermissions';
import { OwnerApprovalChain } from './routes/owner/ApprovalChain';
import { OwnerOwners } from './routes/owner/Owners';
import { OwnerOrgStructure } from './routes/owner/OrgStructure';
import { OwnerCompensation } from './routes/owner/Compensation';
import { NotificationSettings } from './routes/owner/NotificationSettings';
import { OwnerRegisterEmployees } from './routes/owner/RegisterEmployees';
import { RosterImport } from './routes/hr/RosterImport';
import { EmployeeAttendance } from './routes/hr/EmployeeAttendance';
import { Swap } from './routes/Swap';
import { ShiftChange } from './routes/ShiftChange';
import { ChangeShift } from './routes/ChangeShift';
import { AttendanceCorrection } from './routes/AttendanceCorrection';
import { MySchedule } from './routes/MySchedule';
import { MyPayouts } from './routes/MyPayouts';
import { HRPayouts } from './routes/hr/Payouts';
import { HRAllowances } from './routes/hr/Allowances';
import { HRHolidays } from './routes/hr/Holidays';
import { HRContracts } from './routes/hr/Contracts';
import { HREvaluations } from './routes/hr/Evaluations';
import { HREvaluationDetail } from './routes/hr/EvaluationDetail';
import { MyEvaluations } from './routes/MyEvaluations';
import { EvaluationForm } from './routes/EvaluationForm';
import { HRDayoff } from './routes/hr/Dayoff';
import { HRDayoffOverview } from './routes/hr/DayoffOverview';
import { HRAbsenceReport } from './routes/hr/AbsenceReport';
import { HRQuotas } from './routes/hr/Quotas';
import { OfficeGeofenceSettings } from './routes/hr/OfficeGeofence';
import { HRLeavePackages } from './routes/hr/LeavePackages';
import { HRFollowup } from './routes/hr/Followup';
import { HRAllowanceMaster } from './routes/hr/AllowanceMaster';
import { HRCommission } from './routes/hr/Commission';
import { HRShifts } from './routes/hr/Shifts';
import { Approvals } from './routes/Approvals';
import { Tickets } from './routes/Tickets';
// /hr/tickets and /hr/emergency-leave merged into the /approvals hub.
import { ConfirmClockout } from './routes/ConfirmClockout';
import { AskAI } from './routes/AskAI';
import { Find } from './routes/Find';
import { MyRequests } from './routes/MyRequests';
import { MyDayoff } from './routes/MyDayoff';
import { MyTeam } from './routes/MyTeam';
import { TeamSchedule } from './routes/TeamSchedule';
import { HistoricalImport } from './routes/owner/HistoricalImport';
import { ImportEmployees } from './routes/owner/ImportEmployees';
import { OwnerCertificates } from './routes/owner/Certificates';
import { OwnerDocuments } from './routes/owner/Documents';
import { MyDocuments } from './routes/MyDocuments';
import { OwnerOnboarding } from './routes/owner/Onboarding';
import { OwnerOnboardingSetup } from './routes/owner/OnboardingSetup';
import { MyOnboarding } from './routes/MyOnboarding';
import { MyAttendance } from './routes/MyAttendance';
import { HRAttendanceToday } from './routes/hr/AttendanceToday';
import { Factsheet } from './routes/Factsheet';
import { Loading } from './components/layout/Loading';
import { ErrorScreen } from './components/layout/ErrorScreen';
import { PendingApprovalScreen } from './routes/PendingApproval';
import { WebLogin } from './routes/WebLogin';
import { Launcher } from './routes/Launcher';

export function App() {
  const { profile, status, error, refreshAuth } = useAuth();

  if (status === 'loading') return <Loading />;
  // Web-login flow: when no LIFF context is available, route everything to
  // the /login page (no LINE required). User logs in with email+password,
  // setAuth flips status to 'ok', and we re-render into the normal app.
  if (status === 'no-liff') {
    return (
      <Routes>
        <Route path="/login" element={<WebLogin />} />
        {/* สมัครทดลองใช้ด้วยอีเมล+รหัสผ่าน (ไม่ผ่าน LINE) — ใช้ได้จากเบราว์เซอร์ */}
        <Route path="/signup" element={<Signup />} />
        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
    );
  }
  if (status === 'error') return <ErrorScreen message={error ?? 'Unknown error'} onRetry={() => void refreshAuth(true)} />;
  // 'unpaired' = LINE OK but no user row yet → Landing lets them pick
  // signup-as-owner vs pair-as-employee.
  // 'unpaired' (ยังไม่มี user row) หรือ มี jwt ค้างแต่ profile ว่าง → หน้า onboarding.
  // ต้องครอบด้วย <Routes> ทั้งคู่ ไม่งั้นปุ่มใน Landing (Link → /claim /pair /signup)
  // เปลี่ยน URL แต่หน้าไม่เปลี่ยน (เด้งกลับ Landing) = "กดแล้วไม่ไป".
  if (status === 'unpaired' || !profile) {
    return (
      <Routes>
        <Route path="/signup" element={<Signup />} />
        <Route path="/pair" element={<Pair />} />
        <Route path="/claim" element={<Claim />} />
        <Route path="*" element={<Landing />} />
      </Routes>
    );
  }
  // Tenant created but platform admin hasn't approved yet — holding screen.
  if (profile.tenant_status === 'pending_approval') {
    return <PendingApprovalScreen profile={profile} onRefresh={() => void refreshAuth(true)} />;
  }

  // Role-based landing: OWNER → /owner dashboard; HR → /hr; others → Home.
  // Users can still hit /home or /employee links from the role dashboards
  // to access the employee menu (check-in, leave, OT, slip).
  const rootElement =
    profile.role === 'OWNER' ? <Navigate to="/owner" replace />
    : profile.role === 'HR' || profile.role === 'MANAGER' ? <Navigate to="/hr" replace />
    : <Home />;

  return (
    <Routes>
      <Route path="/" element={rootElement} />
      <Route path="/launcher" element={<Launcher />} />
      <Route path="/home" element={<Home />} />
      <Route path="/checkin" element={<Checkin />} />
      <Route path="/leave" element={<LeaveSubmit />} />
      <Route path="/ot" element={<OTSubmit />} />
      <Route path="/swap" element={<Swap />} />
      <Route path="/shift-change" element={<ShiftChange />} />
      <Route path="/change-shift" element={<ChangeShift />} />
      <Route path="/attendance-correction" element={<AttendanceCorrection />} />
      <Route path="/my-schedule" element={<MySchedule />} />
      <Route path="/my-payouts" element={<MyPayouts />} />
      <Route path="/quota" element={<Quota />} />
      <Route path="/policies" element={<Policies />} />
      <Route path="/slip" element={<Slip />} />
      <Route path="/profile" element={<Profile />} />
      <Route path="/my-documents" element={<MyDocuments />} />
      <Route path="/my-onboarding" element={<MyOnboarding />} />
      <Route path="/respond-info/:leaveId" element={<RespondInfo />} />
      <Route path="/approvals" element={<Approvals />} />
      <Route path="/tickets" element={<Tickets />} />
      <Route path="/confirm-clockout" element={<ConfirmClockout />} />
      <Route path="/ask" element={<AskAI />} />
      <Route path="/find" element={<Find />} />
      <Route path="/me/requests" element={<MyRequests />} />
      <Route path="/me/dayoff" element={<MyDayoff />} />
      <Route path="/me/evaluations" element={<MyEvaluations />} />
      <Route path="/me/evaluations/:id" element={<EvaluationForm />} />
      <Route path="/me/team" element={<MyTeam />} />
      <Route path="/team/schedule" element={<TeamSchedule />} />
      <Route path="/me/attendance" element={<MyAttendance />} />
      <Route path="/factsheet" element={<Factsheet />} />
      {/* ผูกบริษัทเพิ่ม (คนเดียวอยู่ได้หลายบริษัท) — เข้าถึงได้ทั้งที่ล็อกอินอยู่
          ผ่านปุ่ม "เพิ่มบริษัท" ใน TenantSwitcher. /auth/pair คืน JWT ของบริษัท
          ใหม่ → setAuth → เด้งเข้าบริษัทที่เพิ่งเพิ่ม. */}
      <Route path="/pair" element={<Pair />} />

      {/* HR (MANAGER has the same operational access as HR) */}
      {(profile.role === 'HR' || profile.role === 'OWNER' || profile.role === 'MANAGER') && (
        <>
          <Route path="/hr" element={<HRDashboard />} />
          <Route path="/owner/guide" element={<WorkflowGuide />} />
          <Route path="/owner/blueprint" element={<OwnerBlueprint />} />
          <Route path="/owner/setup" element={<OwnerSetup />} />
          <Route path="/owner/policies" element={<OwnerPolicies />} />
          <Route path="/owner/hr-permissions" element={<OwnerHrPermissions />} />
          <Route path="/owner/approval-chain" element={<OwnerApprovalChain />} />
          <Route path="/owner/owners" element={<OwnerOwners />} />
          <Route path="/owner/org" element={<OwnerOrgStructure />} />
          <Route path="/owner/comp" element={<OwnerCompensation />} />
          <Route path="/owner/notifications" element={<NotificationSettings />} />
          <Route path="/owner/register-employees" element={<OwnerRegisterEmployees />} />
          <Route path="/owner/import-employees" element={<ImportEmployees />} />
          <Route path="/owner/certificates" element={<OwnerCertificates />} />
          <Route path="/owner/documents" element={<OwnerDocuments />} />
          <Route path="/owner/onboarding" element={<OwnerOnboarding />} />
          <Route path="/owner/onboarding-setup" element={<OwnerOnboardingSetup />} />
          <Route path="/owner/import-historical" element={<HistoricalImport />} />
          <Route path="/hr/roster-import" element={<RosterImport />} />
          <Route path="/hr/attendance-today" element={<Navigate to="/hr/time#today" replace />} />
          {/* Legacy approval pages → redirect to consolidated /approvals hub
              (preserved so old DailyBriefing/LIFF rich-menu deep links still resolve). */}
          <Route path="/hr/pending" element={<Navigate to="/approvals#changes" replace />} />
          <Route path="/hr/tickets" element={<Navigate to="/approvals#tickets" replace />} />
          <Route path="/hr/emergency-leave" element={<Navigate to="/approvals#emergency" replace />} />
          {/* People hub: employees + onboard + termination + followup + pending-changes */}
          <Route path="/hr/people" element={<People />} />
          <Route path="/hr/contracts" element={<HRContracts />} />
          <Route path="/hr/evaluations" element={<HREvaluations />} />
          <Route path="/hr/evaluations/:cycleId" element={<HREvaluationDetail />} />
          {/* Time hub: settings + shifts + attendance-today + holidays + leave-packages */}
          <Route path="/hr/time" element={<Time />} />
          <Route path="/hr/dayoff" element={<HRDayoff />} />
          <Route path="/hr/dayoff/overview" element={<HRDayoffOverview />} />
          <Route path="/hr/attendance" element={<EmployeeAttendance />} />
          <Route path="/hr/absence" element={<HRAbsenceReport />} />
          <Route path="/hr/employees" element={<Navigate to="/hr/people#all" replace />} />
          <Route path="/hr/followup" element={<Navigate to="/hr/people#followup" replace />} />
          <Route path="/hr/allowances" element={<HRAllowances />} />
          <Route path="/hr/holidays" element={<Navigate to="/hr/time#holidays" replace />} />
          <Route path="/hr/quotas" element={<HRQuotas />} />
          <Route path="/hr/office-location" element={<OfficeGeofenceSettings />} />
          <Route path="/hr/leave-packages" element={<Navigate to="/hr/time#leave" replace />} />
          {/* /hr/followup → redirected above to /hr/people#followup */}
          <Route path="/hr/allowance-master" element={<Navigate to="/owner/wage#allowance" replace />} />
          {/* Wage hub: analysis + commission settings + year-end + allowance + deduction */}
          <Route path="/owner/wage" element={<Wage />} />
          {/* Commission: packages + runs merged into single hub with tabs. */}
          <Route path="/hr/commission" element={<HRCommission />} />
          <Route path="/hr/payouts" element={<HRPayouts />} />
          {/* packages ย้ายไปอยู่ใน Wage hub แล้ว; commission hub เหลือแค่ runs */}
          <Route path="/hr/commission-packages" element={<Navigate to="/owner/wage#commission" replace />} />
          <Route path="/hr/commission-runs" element={<Navigate to="/hr/commission" replace />} />
          <Route path="/hr/shifts" element={<Navigate to="/hr/time#shifts" replace />} />
          {/* Payroll prep is HR's job (reconcile + run draft + submit for approval).
              Owner-only actions (approve / send slips) gate on the server. */}
          {/* Payroll cycle: reconcile + close-readiness + run merged into single hub with tabs. */}
          <Route path="/owner/cycle" element={<PayrollCycle />} />
          <Route path="/owner/payroll/history" element={<OwnerPayrollHistory />} />
          <Route path="/owner/reconcile" element={<Navigate to="/owner/cycle#reconcile" replace />} />
          <Route path="/owner/close-readiness" element={<Navigate to="/owner/cycle#close" replace />} />
          <Route path="/owner/payroll" element={<Navigate to="/owner/cycle#payroll" replace />} />
          <Route path="/owner/payroll-analysis" element={<Navigate to="/owner/wage#analysis" replace />} />
        </>
      )}

      {/* Supervisor (หัวหน้างาน) — เห็นเฉพาะหน้าจัดการทีมตัวเอง (scope ลูกทีมที่ backend).
          ไม่เปิด owner routes อื่น (เงินเดือน/ตั้งค่า) เพื่อกันเข้าถึงข้ามสิทธิ์. */}
      {profile.role === 'SUPERVISOR' && (
        <>
          <Route path="/hr" element={<HRDashboard />} />
          <Route path="/hr/time" element={<Time />} />
          <Route path="/hr/attendance" element={<EmployeeAttendance />} />
          <Route path="/hr/roster-import" element={<RosterImport />} />
          <Route path="/hr/shifts" element={<Navigate to="/hr/time#shifts" replace />} />
          <Route path="/hr/attendance-today" element={<Navigate to="/hr/time#today" replace />} />
        </>
      )}

      {/* Owner */}
      {profile.role === 'OWNER' && (
        <>
          <Route path="/owner" element={<OwnerDashboard />} />
          <Route path="/owner/onboard" element={<Navigate to="/hr/people#onboard" replace />} />
          <Route path="/owner/drive" element={<Navigate to="/owner/settings#drive" replace />} />
          <Route path="/owner/audit" element={<Navigate to="/owner/settings#audit" replace />} />
          <Route path="/owner/pending-changes" element={<Navigate to="/hr/people#changes" replace />} />
          <Route path="/owner/deductions" element={<Navigate to="/owner/wage#deduction" replace />} />
          <Route path="/owner/settings" element={<OwnerSettings />} />
          <Route path="/owner/templates" element={<OwnerTemplates />} />
          <Route path="/owner/ai" element={<Navigate to="/owner/settings#ai" replace />} />
          <Route path="/owner/year-end" element={<OwnerYearEnd />} />
          <Route path="/owner/termination" element={<Navigate to="/hr/people#termination" replace />} />
        </>
      )}

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
